import React, { useEffect, useMemo, useState } from 'react';
import { stdHeaderRow, stdPageTitle, stdPageSubtitle } from './adminlayout/designStandard';
import { useTicketTypeManagement } from './tickettypemanagement/useTicketTypeManagement';
import { PersonsAndWorkflowSection } from './tickettypemanagement/PersonsAndWorkflowSection';
import { OperationsAndSettlementSection } from './tickettypemanagement/OperationsAndSettlementSection';

// 2026-09-20 두 탭을 옮겼다 — 같은 기능이 두 군데 있으면 한쪽만 고치는 실수가 난다.
//   '상품 설정'  → 상품 관리 화면 (카테고리 이름과 회사 연결이 떨어져 있던 것이 사고의 원인이었다)
//   '회사 설정'  → 테넌트 관리 > 회사 (활성 토글·코드 변경을 그쪽으로 옮겼다)
const SECTIONS = [
  {
    key: 'sales',
    label: '영업 운영',
    desc: '최근계약',
  },
  {
    key: 'settlement',
    label: '정산 설정',
    desc: '매출 반영률, 원가, 배분율 이력',
  },
];

function SectionNav({ activeTab, setActiveTab }) {
  return (
    <nav className="bg-white dark:bg-pine-900 border border-[var(--border)] rounded-xl overflow-hidden">
      <div className="px-4 py-3 border-b border-[var(--border)]">
        <div className="text-[13px] font-black text-[var(--text)]">설정 분류</div>
        <div className="mt-0.5 text-[11px] text-[var(--text-muted)]">업무 흐름별로 나눠 관리합니다.</div>
      </div>
      <div className="hidden lg:grid p-2 gap-1.5">
        {SECTIONS.map((tab) => {
          const active = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveTab(tab.key)}
              className={[
                'text-left rounded-lg border px-3 py-2 transition-colors',
                active
                  ? 'bg-accent-50 dark:bg-accent-900/20 border-transparent'
                  : 'bg-transparent border-transparent hover:bg-[var(--bg-subtle)]',
              ].join(' ')}
            >
              <div className={active ? 'text-[13px] font-black text-accent-600 dark:text-accent-300' : 'text-[13px] font-extrabold text-[var(--text)]'}>
                {tab.label}
              </div>
              <div className="mt-0.5 text-[11px] font-semibold text-[var(--text-muted)] leading-snug">
                {tab.desc}
              </div>
            </button>
          );
        })}
      </div>
      <div className="lg:hidden overflow-x-auto p-2">
        <div className="flex gap-1.5 min-w-max">
          {SECTIONS.map((tab) => {
            const active = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(tab.key)}
                className={[
                  'h-9 rounded-lg px-3 text-[12px] font-extrabold border transition-colors',
                  active
                    ? 'bg-accent-50 dark:bg-accent-900/20 text-accent-600 dark:text-accent-300 border-transparent'
                    : 'bg-[var(--bg-subtle)] text-[var(--text-muted)] border-[var(--border)]',
                ].join(' ')}
              >
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>
    </nav>
  );
}

export default function TicketTypeManagement() {
  const state = useTicketTypeManagement();
  const [activeTab, setActiveTab] = useState('sales');
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth < 768);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const activeInfo = useMemo(
    () => SECTIONS.find((section) => section.key === activeTab) || SECTIONS[0],
    [activeTab],
  );

  // 휴대폰은 공통 틀(AdminLayout main)이 좌우 16px 을 준다 — 겹치지 않게 0 (2026-09-28)
  return (
    <div className="max-w-[1180px] mx-auto grid gap-4 px-0">
      <header style={stdHeaderRow(isMobile)}>
        <div>
          <h2 style={stdPageTitle}>동적 관리</h2>
          <div style={stdPageSubtitle}>영업 운영·정산 설정을 관리합니다. 상품·카테고리는 상품 관리, 회사는 테넌트 관리 화면에 있습니다.</div>
        </div>
        <div className="inline-flex w-fit items-center rounded-full bg-[var(--bg-subtle)] px-3 py-1 text-[11px] font-extrabold text-[var(--text-muted)]">
          현재 화면: {activeInfo.label}
        </div>
      </header>

      <div className="grid gap-4 lg:grid-cols-[230px_minmax(0,1fr)]">
        <SectionNav activeTab={activeTab} setActiveTab={setActiveTab} />
        <main className="grid gap-4 min-w-0">
          {/* 피드백 옵션·미팅인계·배너 문구는 미팅지도 관리 ⚙️ 설정 모달로 이관 (2026-09-01)
              회사별 일일콜 표시는 상품 관리 > "회사에 붙이기" 로 이관 (2026-09-20) */}
          {activeTab === 'sales' && (
            <PersonsAndWorkflowSection {...state} />
          )}

          {activeTab === 'settlement' && (
            <OperationsAndSettlementSection {...state} />
          )}
        </main>
      </div>
    </div>
  );
}
